/**
 * Dashboard - at-a-glance ops overview. See frontend spec §5.1.
 *
 * Visual/operational hierarchy (per product direction): the first question
 * this page answers is "is the system healthy?", not "who changed something
 * recently?". Readiness (the authoritative health signal) leads; sync state
 * follows; consumer counts and recent admin activity are secondary.
 *
 * No panel ever fabricates data: every value here comes straight from a
 * backend response, and every panel has its own loading/empty/error/
 * unavailable state so one failing endpoint never blanks out the others.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderUnavailableState, renderEmptyState } from "../components/states";
import { renderStatusText, checkTone, runStatusTone } from "../components/statusText";
import { renderAbsoluteTime, renderRelativeTime } from "../components/dateTime";
import { absoluteTime, entityLabel, ENTITY_ORDER, formatNumber, resourceTypeLabel, actionLabel, actorTypeLabel, EMPTY } from "../utils/format";
import { getHealthInfo, getLiveness, getReadiness } from "../services/health";
import { getSyncStatus } from "../services/sync";
import { listServices, getSyncSettings } from "../services/admin";
import { listAudit } from "../services/audit";
import { ApiError } from "../services/apiClient";
import type { AuditItem, SyncStatusRow } from "../types/api";

const REFRESH_INTERVAL_MS = 30_000;

interface Panel {
  root: HTMLElement;
  refresh: () => Promise<void>;
}

export class DashboardPage {
  private intervalId: number | null = null;
  private panels: Panel[] = [];

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    const health = this.buildHealthPanel();
    const sync = this.buildSyncPanel(navigate);
    const consumersAndCron = this.buildConsumerAndCronRow();
    const activity = this.buildActivityPanel(navigate);

    this.panels = [health, sync, consumersAndCron, activity];

    const refreshButton = el(
      "button",
      { className: "btn btn-ghost btn-sm", type: "button", onclick: () => void this.refreshAll() },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Dashboard",
          subtitle: "A quick read on platform health, sync activity, and the latest administrative changes.",
          actions: [refreshButton],
        }),
        health.root,
        sync.root,
        consumersAndCron.root,
        activity.root,
      ])
    );

    void this.refreshAll();
    this.intervalId = window.setInterval(() => void this.refreshAll(), REFRESH_INTERVAL_MS);
  }

  destroy(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.panels = [];
  }

  private async refreshAll(): Promise<void> {
    await Promise.all(this.panels.map((panel) => panel.refresh()));
  }

  /* ---------------- Health (dominant) ---------------- */

  private buildHealthPanel(): Panel {
    const body = el("div", {}, [renderLoadingState("Checking system health…")]);

    const root = el("div", { className: "panel panel--dominant" }, [
      el("p", { className: "panel__title" }, ["System Health"]),
      el("p", { className: "panel__description" }, [
        "Whether the platform can serve traffic right now.",
      ]),
      body,
    ]);

    const refresh = async () => {
      const [readyResult, liveResult, infoResult] = await Promise.all([
        getReadiness(),
        getLiveness(),
        getHealthInfo(),
      ]);

      if (!readyResult.body) {
        mount(body, renderErrorState("Could not reach /health/ready.", { onRetry: () => void refresh() }));
        return;
      }

      const ready = readyResult.body;
      const primaryTone =
        ready.status === "ok" ? "health-panel__primary-value--ok" : "health-panel__primary-value--degraded";

      const dbTone = checkTone(ready.checks?.database);
      const kekaTone = checkTone(ready.checks?.kekaAuth);

      const liveOk = liveResult.body?.status === "ok";
      const liveTone = liveResult.body ? (liveOk ? "success" : "danger") : "muted";
      const liveLabel = liveResult.body ? (liveOk ? "process live" : "process not responding") : "process unknown";

      const version = infoResult.body?.version;
      const uptime = infoResult.body ? formatUptime(infoResult.body.uptimeSeconds) : null;

      mount(
        body,
        el("div", { className: "health-panel__body" }, [
          el("div", { className: "health-panel__primary" }, [
            el("span", { className: "health-panel__primary-label" }, ["System status"]),
            el("span", { className: `health-panel__primary-value ${primaryTone}` }, [
              ready.status === "ok" ? "READY" : "DEGRADED",
            ]),
            el("span", { className: "health-panel__primary-hint" }, [
              ready.status === "ok"
                ? "All system checks are passing."
                : "One or more system checks are failing. Details are below.",
            ]),
          ]),
          el("div", { className: "health-panel__checks" }, [
            el("div", { className: "health-check" }, [
              el("div", { className: "health-check__label" }, ["Database"]),
              renderStatusText(dbTone.label, dbTone.tone),
            ]),
            el("div", { className: "health-check" }, [
              el("div", { className: "health-check__label" }, ["Keka connection"]),
              renderStatusText(kekaTone.label, kekaTone.tone),
            ]),
          ]),
        ])
      );

      // Footer: secondary process signal + version/uptime - never presented
      // as overall health, just supporting context.
      body.append(
        el("div", { className: "health-panel__footer" }, [
          el("span", { className: "health-panel__footer-item" }, [renderStatusText(liveLabel, liveTone)]),
          version ? el("span", { className: "health-panel__footer-item" }, [`version ${version}`]) : null,
          uptime ? el("span", { className: "health-panel__footer-item" }, [`uptime ${uptime}`]) : null,
        ])
      );
    };

    return { root, refresh };
  }

  /* ---------------- Sync summary (standard) ---------------- */

  private buildSyncPanel(navigate: (path: string) => void): Panel {
    const body = el("div", {}, [renderLoadingState("Loading sync status…")]);

    const root = el("div", { className: "panel panel--standard" }, [
      el("p", { className: "panel__title" }, ["Sync summary"]),
      el("p", { className: "panel__description" }, ["One row per entity, in chain order."]),
      body,
      el("div", { className: "dashboard__activity-footer" }, [
        el(
          "button",
          { className: "btn-link", type: "button", onclick: () => navigate("/sync") },
          ["View Sync Monitor"],
        ),
      ]),
    ]);

    const refresh = async () => {
      try {
        const rows = await getSyncStatus();
        if (!rows.length) {
          mount(body, renderEmptyState("No sync data yet."));
          return;
        }
        mount(body, renderSyncTable(rows));
      } catch (err) {
        mount(body, renderErrorState(errorMessage(err), { onRetry: () => void refresh() }));
      }
    };

    return { root, refresh };
  }

  /* ---------------- Consumer counts + next cron (utility pair) ---------------- */

  private buildConsumerAndCronRow(): Panel {
    const consumerBody = el("div", {}, [renderLoadingState("Loading…")]);
    const cronBody = el("div", {}, [renderLoadingState("Loading…")]);

    const root = el("div", { className: "panel-row-2up" }, [
      el("div", { className: "panel panel--utility" }, [
        el("p", { className: "panel__title" }, ["Service Accounts"]),
        consumerBody,
      ]),
      el("div", { className: "panel panel--utility" }, [
        el("p", { className: "panel__title" }, ["Sync schedule"]),
        cronBody,
      ]),
    ]);

    const refreshConsumers = async () => {
      try {
        const services = await listServices();
        const total = services.length;
        const active = services.filter((s) => s.isActive).length;
        const inactive = total - active;
        mount(
          consumerBody,
          el("div", { className: "stat-row" }, [
            statItem(total, "Total"),
            statItem(active, "Active"),
            statItem(inactive, "Inactive"),
          ])
        );
      } catch (err) {
        mount(consumerBody, renderUnavailableState(errorMessage(err)));
      }
    };

    const refreshCron = async () => {
      try {
        const settings = await getSyncSettings();

        const nextRunNode = settings.nextFireAtIst
          ? el("div", { className: "callout", title: settings.nextFireAtIst }, [
              el("span", { className: "callout__label" }, ["Next run"]),
              el("span", { className: "callout__value" }, [absoluteTime(settings.nextFireAtIst)]),
            ])
          : el("p", { className: "next-cron__empty" }, [
              settings.syncEnabled ? "Not yet computed." : "Sync is turned off, so nothing is scheduled.",
            ]);

        mount(
          cronBody,
          el("div", {}, [
            nextRunNode,
            el("div", { className: "mt-5" }, [
              cronRow(
                "Last chain",
                settings.lastChainStatus ? renderStatusText(...runStatusToneTuple(settings.lastChainStatus)) : el("span", {}, [EMPTY])
              ),
              cronRow("Last completed", renderAbsoluteTime(settings.lastChainCompletedAt)),
            ]),
          ])
        );
      } catch (err) {
        mount(cronBody, renderUnavailableState(errorMessage(err)));
      }
    };

    const refresh = async () => {
      await Promise.all([refreshConsumers(), refreshCron()]);
    };

    return { root, refresh };
  }

  /* ---------------- Recent administrative activity ---------------- */

  private buildActivityPanel(navigate: (path: string) => void): Panel {
    const body = el("div", {}, [renderLoadingState("Loading recent activity…")]);

    const root = el("div", { className: "panel panel--standard" }, [
      el("p", { className: "panel__title" }, ["Recent administrative activity"]),
      el("p", { className: "panel__description" }, ["Recent changes made through this console."]),
      body,
      el("div", { className: "dashboard__activity-footer" }, [
        el(
          "button",
          { className: "btn-link", type: "button", onclick: () => navigate("/audit") },
          ["View Audit Trail"],
        ),
      ]),
    ]);

    const refresh = async () => {
      try {
        const data = await listAudit({ limit: 5, offset: 0 });
        if (!data.items.length) {
          mount(body, renderEmptyState("No administrative changes yet."));
          return;
        }
        mount(body, renderActivityList(data.items));
      } catch (err) {
        // Recent Activity degrades independently: unreachable, disabled
        // (401/503), or erroring never blocks the rest of the dashboard, and
        // never fabricates rows/timestamps/actors.
        if (err instanceof ApiError && err.status === 503) {
          mount(
            body,
            renderUnavailableState(
              "Audit history is not available. Admin access may not be set up on this server."
            )
          );
        } else if (err instanceof ApiError && err.status === 401) {
          mount(
            body,
            renderUnavailableState("Audit history is unavailable. Sign in with Microsoft to continue.")
          );
        } else {
          mount(
            body,
            renderUnavailableState("Recent administrative activity could not be loaded.", errorMessage(err))
          );
        }
      }
    };

    return { root, refresh };
  }
}

/* ---------------- render helpers ---------------- */

function renderSyncTable(rows: SyncStatusRow[]): HTMLElement {
  const order = ENTITY_ORDER as readonly string[];
  const ordered = [...rows].sort((a, b) => order.indexOf(a.entity) - order.indexOf(b.entity));

  const table = el("table", { className: "data-table" }, [
    el("thead", {}, [
      el("tr", {}, [
        el("th", { className: "data-table__col--entity" }, ["Data type"]),
        el("th", { className: "data-table__col--status" }, ["Status"]),
        el("th", { className: "data-table__col--relative" }, ["Last finished"]),
        el("th", { className: "numeric data-table__col--numeric" }, ["Records"]),
      ]),
    ]),
    el(
      "tbody",
      {},
      ordered.map((row) => {
        const tone = runStatusTone(row.lastRunStatus);
        const flagClass = tone.tone === "danger" ? "flag-danger" : tone.tone === "accent" ? "flag-accent" : "";
        return el("tr", { className: flagClass || undefined }, [
          el("td", {}, [entityLabel(row.entity)]),
          el("td", {}, [renderStatusText(tone.label, tone.tone, { pulse: tone.pulse })]),
          el("td", {}, [renderRelativeTime(row.lastRunCompletedAt)]),
          el("td", { className: "numeric" }, [formatNumber(row.recordsProcessed)]),
        ]);
      })
    ),
  ]);

  return el("div", { className: "data-table-wrap" }, [table]);
}

function renderActivityList(items: AuditItem[]): HTMLElement {
  return el(
    "div",
    { className: "activity-list" },
    items.map((item) =>
      el("div", { className: "activity-row" }, [
        el("span", { className: "activity-row__time" }, [renderRelativeTime(item.occurredAt)]),
        el("div", { className: "activity-row__body" }, [
          el("span", { className: "activity-row__action" }, [actionLabel(item.action)]),
          el("span", { className: "activity-row__meta" }, [
            [resourceTypeLabel(item.resourceType), item.resourceName].filter(Boolean).join(" · ") || EMPTY,
          ]),
        ]),
        el("span", { className: "activity-row__actor" }, [actorTypeLabel(item.actorType)]),
      ])
    )
  );
}

function statItem(value: number, label: string): HTMLElement {
  return el("div", { className: "stat-item" }, [
    el("span", { className: "stat-item__value" }, [formatNumber(value)]),
    el("span", { className: "stat-item__label" }, [label]),
  ]);
}

function cronRow(label: string, value: HTMLElement): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    value,
  ]);
}

function runStatusToneTuple(status: string): [string, "success" | "danger" | "accent" | "muted"] {
  const tone = runStatusTone(status);
  return [tone.label, tone.tone];
}

function formatUptime(seconds: number | undefined): string | null {
  if (seconds === undefined || seconds === null || Number.isNaN(seconds)) return null;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
